import React from "react";
import {
    BackButton,
    PrimaryButton,
    SecondaryButton
} from "../simple-buttons/simple-buttons";
import styled, { css } from "styled-components";
import defaultTheme from "./theme-test-override";
import {
    grid,
    GridProps,
    layout,
    LayoutProps,
    space,
    SpaceProps
} from "styled-system";
import { isMobile, media } from "./style-utils-coras-nua";

type ActionButtonContainerProps = {
    back?: string;
    children: React.ReactNode;
};
type ActionButtonProps = {
    label?: string;
    variant?: string;
    url?: string;
    className?: string;
    target?: string;
};
type GridTypes = LayoutProps &
    SpaceProps &
    GridProps & { gridTemplateColumns?: number } & {
        gridTemplateColumnsMobile?: number;
    } & { gridGap?: string } & { gridGapMobile?: string };

const ActionButtonContainer = ({
    back,
    gridTemplateColumns,
    gridTemplateColumnsMobile = 1,
    children
}: ActionButtonContainerProps & GridTypes) => (
    <Container
        back={back}
        gridTemplateColumns={gridTemplateColumns}
        gridTemplateColumnsMobile={gridTemplateColumnsMobile}
    >
        {back && !isMobile() ? (
            <BackButton
                as="a"
                href={back}
                title={`Return to listings`}
                rel={`noreferrer`}
            >
                Back
            </BackButton>
        ) : (
            ""
        )}
        {children}
        {back && isMobile() ? (
            <BackButton
                as="a"
                href={back}
                title={`Return to listings`}
                rel={`noreferrer`}
            >
                Back
            </BackButton>
        ) : (
            ""
        )}
    </Container>
);

const ActionButton = ({
    label,
    variant,
    url,
    className = "",
    target
}: ActionButtonProps) =>
    variant === "secondary" ? (
        <SecondaryButton
            className={className}
            as="a"
            href={url}
            title={label}
            target={target}
            rel={`${target === "_blank" ? "noopener" : ""} noreferrer`}
            text={label}
        />
    ) : variant === "back" ? (
        <BackButton
            className={className}
            as="a"
            href={url}
            title={label}
            target={target}
            rel={`${target === "_blank" ? "noopener" : ""} noreferrer`}
        >
            {label}
        </BackButton>
    ) : (
        <PrimaryButton
            className={className}
            as="a"
            href={url}
            title={label}
            target={target}
            rel={`${target === "_blank" ? "noopener" : ""} noreferrer`}
            text={label}
        />
    );

const Container = styled("div")<GridTypes & ActionButtonContainerProps>`
    ${layout}
    ${grid}
    ${space}
    ${props =>
        props.gridTemplateColumns &&
        props.gridTemplateColumnsMobile &&
        css`
            grid-template-columns: repeat(${props.gridTemplateColumns}, 1fr);
            ${media.sm.down`
                grid-template-columns: repeat(
                    ${props.gridTemplateColumnsMobile},
                    1fr
                );
            `}
        `}
    grid-auto-flow: column; /* tells to add new children as new columns*/
    grid-auto-columns: 1fr; /* tells the size of the dynamic columns*/
    ${media.sm.down`
        grid-auto-flow: row;
    `}
    > a {
        margin: 0;
        max-width: 100%;
        min-width: 100%;
        &:only-child {
            max-width: calc(50% - 0.5rem);
            min-width: calc(50% - 0.5rem);
            margin-left: auto;
            ${media.sm.down`
                max-width: 100%;
                min-width: 100%;
            `}
        }
    }

`;
Container.defaultProps = {
    display: "grid",
    gridGap: "1rem",
    marginBottom: "3rem",
    marginTop: "3rem",
    gridTemplateColumns: 1,
    theme: {
        ...defaultTheme
    }
};

export { ActionButton, ActionButtonContainer };
